import type { SupabaseClient } from '@supabase/supabase-js';
import {
  KF_ICON,
  KF_LOGO_GOECOMMERCE,
  KF_LOGO_KAUFLAND,
  kfFlagSvg,
  showToast,
} from '@vario-connect/frontend-design';
import {
  renderListingDetailPane,
  STATUS_PILL,
  listingPill,
  wireTwoStepConfirm,
  type UnitState,
  STOREFRONT_LABELS,
  type ListingStatus,
} from '../shared/listing-detail-pane.js';
import { bestimmeModusWahl } from '../../lib/listing-mode-choice.js';

// ============================================================================
// Kaufland Lister — Inbox-Flow (analog Bestellungen-UI).
//
// Konzept:
//   * Die Inbox kaufland.listings wird ausschliesslich vom Vario-Slot befuellt:
//     wenn der User im Vario-UI im Verkaufskanal-Slot eine Kaufland-Kategorie
//     assigniert, schreibt der Slot via RPC kaufland_upsert_listing_from_slot
//     eine neue Row. Kein Hintergrund-Scan, kein Cron, kein "Aus Vario
//     nachladen"-Button — die User-Aktion im Slot ist der einzige Schreibpfad.
//   * Diese Maske ist NUR der Master: Inbox-Liste, Filter, Suche, Pager.
//     Das Detail rechts (Tabs Mapping/Bilder/Optional/Preview + Aufschalt-
//     Panel) rendert das geteilte Modul views/shared/listing-detail-pane.ts —
//     dasselbe Pane laeuft auch im Vario-Slot (listing-status, Configure-Modus).
//   * Status-Lifecycle: pending → mapping → mapped → queued → published.
// ============================================================================

interface KauflandListCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

/** Slot-Ctx wie im Vario-Sales-Channels-Tab (analog OffersSlotCtx). */
interface KauflandListSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Einstieg: Lister als eigener `settings.sales-channels.{appId}.tabs`-Tab
 *  (→ /#/list). Rendert die Maske im kf-page-Design (apphead + Filterbar + Master-Liste),
 *  Detail-Pane bleibt das geteilte renderListingDetailPane. */
export function renderKauflandList(ctx: KauflandListSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderKauflandListTab(
    ctx.contentEl,
    { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl },
    { standalone: true },
  );
}

/** ListingStatus → kf-page-Status-Pill-Klasse (Punkt + Text). Labels kommen aus
 *  STATUS_PILL (geteilt mit dem Detail-Pane), die Farbe aus diesen Klassen. */
const STATUS_CLS: Record<ListingStatus, string> = {
  pending: 'st-pending',
  mapping: 'st-mapping',
  mapped: 'st-mapped',
  queued: 'st-queued',
  validating: 'st-queued',
  published: 'st-published',
  error: 'st-error',
  archived: 'st-archived',
};

// Statische Inline-SVGs aus dem Design-Handoff "Kaufland Lister" (currentColor, 24×24).
const TAG_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><path d="M7 7h.01"/></svg>';
const WARN_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>';
const SPARK_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4"/></svg>';
const PG_FIRST =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m11 17-5-5 5-5M18 17l-5-5 5-5"/></svg>';
const PG_PREV =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg>';
const PG_NEXT =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>';
const PG_LAST =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m13 17 5-5-5-5M6 17l5-5-5-5"/></svg>';

interface ListingRow {
  id: string;
  vario_article_id: string;
  vario_listing_id: string;
  vario_article_number: string | null;
  vario_article_name: string | null;
  storefront: string;
  vario_category_name: string | null;
  kaufland_category_id: number | null;
  status: ListingStatus;
  is_variant_parent: boolean;
  last_error: string | null;
  last_pushed_at: string | null;
  first_seen_at: string;
  updated_at: string;
  total_count: number;
  /** Aufgehydratet via per-Page-Lookup auf kaufland.listings (siehe
   *  hydrateModes()), weil die RPC kaufland_list_listings die Spalte nicht
   *  liefert. Fallback 'new', wenn der Lookup leer/failed. */
  mode?: 'new' | 'attach' | 'undecided';
  /** Kaufland-Katalogtreffer zur EAN, aus derselben Hydration wie `mode`. Nur gesetzt, wenn
   *  ein Katalogeintrag existiert -- Voraussetzung fuer 'attach' (siehe bestimmeModusWahl in
   *  lib/listing-mode-choice.ts und teileAuswahlNachModus weiter unten). */
  attach_id_product?: number | null;
  /** Kauflands letzte Rueckmeldung waehrend der Pruefung, aus derselben Hydration wie
   *  `mode`. Nur bei status='validating' von Belang: dort steht sonst ausschliesslich
   *  "In Pruefung" und der Haendler sieht tagelang nicht, WORAUF gewartet wird. */
  kaufland_status_reason?: string | null;
  /** Zustand der Kaufland-Unit, aus derselben Hydration. Nur bei status='published' von
   *  Belang: "Gelistet" heisst sonst auch dann "Gelistet", wenn niemand kaufen kann. */
  unit?: UnitState | null;
}

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '',           label: 'Alle (offen)' },
  { value: 'pending',    label: 'Neu' },
  { value: 'mapping',    label: 'In Bearbeitung' },
  { value: 'mapped',     label: 'Bereit' },
  { value: 'error',      label: 'Fehler' },
  { value: 'validating', label: 'In Prüfung' },
  { value: 'published',  label: 'Gelistet' },
  { value: 'archived',   label: 'Archiviert' },
];

type ModeFilterValue = 'all' | 'new' | 'attach' | 'undecided';

const MODE_FILTER_OPTIONS: Array<{ value: ModeFilterValue; label: string }> = [
  { value: 'all',    label: 'Alle Modi' },
  { value: 'new',    label: 'Neu listen' },
  { value: 'attach', label: 'Aufschalten' },
  { value: 'undecided', label: 'Entscheidung ausstehend' },
];

export interface AuswahlZeile { id: string; attach_id_product: number | null }

/**
 * Trennt eine Mehrfachauswahl in das, was der gewuenschte Modus zulaesst, und den Rest.
 *
 * Bewusst kein stilles Ueberspringen: Wer fuenfzig Zeilen markiert und dreissig davon
 * still nicht gesetzt bekommt, haelt hinterher alle fuer entschieden.
 *
 * Fix (Review 2026-08-24): frueher stand hier eine zweite, eigene Herleitung von
 * "attach_id_product == null -> attach unmoeglich" -- exakt die Beurteilung, die
 * bestimmeModusWahl() (lib/listing-mode-choice.ts) bereits fuer die Einzel-Entscheidung
 * im Detail-Pane trifft. Zwei Kopien derselben Regel laufen frueher oder spaeter auseinander;
 * jetzt gibt es nur noch die eine.
 */
export function teileAuswahlNachModus(
  zeilen: AuswahlZeile[],
  modus: 'attach' | 'new',
): { machbar: AuswahlZeile[]; nichtMachbar: AuswahlZeile[] } {
  if (modus === 'new') return { machbar: [...zeilen], nichtMachbar: [] };
  const machbar = zeilen.filter((z) => bestimmeModusWahl(z.attach_id_product).attachMoeglich);
  const nichtMachbar = zeilen.filter((z) => !bestimmeModusWahl(z.attach_id_product).attachMoeglich);
  return { machbar, nichtMachbar };
}

/**
 * Trennt eine Auswahl in das, was gerade sichtbar ist, und das, was durch einen aktiven
 * Client-Filter (Freitextsuche) ausgeblendet ist.
 *
 * Fix (Review 2026-08-24, Important 1): "Seite markieren" und die Sammelaktion griffen
 * bisher auf `lastRows`/`selectedIds` zu, ohne die Suche zu beruecksichtigen -- die Suche
 * setzt nur `row.hidden` im DOM, sie veraendert weder die Row-Liste noch die Auswahl. Tippt
 * der Haendler "abc" (3 von 50 Karten sichtbar) und klickt dann "Seite markieren", markierte
 * das bisher alle 50; eine anschliessende Sammelaktion aenderte Zeilen, die er nie gesehen
 * hat -- bei 'new' unumkehrbar. Diese Funktion ist bewusst generisch und DOM-frei gehalten,
 * damit sie ohne Browser testbar ist; der DOM-Zugriff (welche IDs gerade sichtbar sind)
 * passiert ausschliesslich im Aufrufer (siehe visibleRowIds() weiter unten).
 */
export function filtereSichtbareAuswahl<T extends { id: string }>(
  zeilen: T[],
  sichtbareIds: ReadonlySet<string>,
): { sichtbar: T[]; ausgeblendet: T[] } {
  return {
    sichtbar: zeilen.filter((z) => sichtbareIds.has(z.id)),
    ausgeblendet: zeilen.filter((z) => !sichtbareIds.has(z.id)),
  };
}

interface FilterState {
  storefront: string;
  status: string;
  /** Modus-Filter: 'all' = unfiltered, sonst 'new' | 'attach'. Clientseitig
   *  angewendet, weil das kaufland_list_listings-RPC den mode weder zurueckliefert
   *  noch als Filter akzeptiert. TODO (Migration 0116): RPC um _mode_filter
   *  erweitern, damit Pagination/Counts korrekt sind. */
  mode: ModeFilterValue;
  /** Frei-Such-Term ueber SKU/Name/Kategorie. Client-side Filter ueber die
   *  geladenen Rows (PAGE_SIZE), kein RPC-Roundtrip — der RPC ist nicht
   *  trivial erweiterbar und 50 Rows pro Page sind ausreichend filterbar. */
  search: string;
}

const PAGE_SIZE = 50;

export async function renderKauflandListTab(
  host: HTMLElement,
  ctx: KauflandListCtx,
  opts: { standalone?: boolean } = {},
) {
  const standalone = !!opts.standalone;
  host.innerHTML = renderShell(standalone);

  const root = host.querySelector<HTMLElement>('.kf-page')!;
  if (standalone) {
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();
  }

  const state: FilterState = { storefront: '', status: '', mode: 'all', search: '' };
  let activePage = 0;
  let activeListingId: string | null = null;
  let lastRows: ListingRow[] = [];
  // Mehrfachauswahl fuer die Sammelaktion (Task 7). Analog zu offers.ts: strikt
  // seitenlokal -- bei Filter- oder Seitenwechsel verworfen (siehe clearSelection()
  // in den jeweiligen Handlern unten), sonst wirkt eine Sammelaktion ueberraschend
  // auf Zeilen, die der Haendler gar nicht mehr sieht.
  const selectedIds = new Set<string>();

  // Storefront-Filter befüllen
  await populateStorefronts(ctx, host);

  // Event-Wiring -----------------------------------------------------------
  const sfSel = host.querySelector<HTMLSelectElement>('#klst-sf')!;
  const statusSel = host.querySelector<HTMLSelectElement>('#klst-status')!;
  const modeSel = host.querySelector<HTMLSelectElement>('#klst-mode')!;
  const pgFirst = host.querySelector<HTMLButtonElement>('#klst-pg-first')!;
  const pgPrev = host.querySelector<HTMLButtonElement>('#klst-pg-prev')!;
  const pgNext = host.querySelector<HTMLButtonElement>('#klst-pg-next')!;
  const pgLast = host.querySelector<HTMLButtonElement>('#klst-pg-last')!;

  // Status-Filter mit Optionen befüllen
  statusSel.innerHTML = STATUS_FILTER_OPTIONS.map((o) => `<option value="${o.value}">${o.label}</option>`).join('');
  // Modus-Filter mit Optionen befüllen
  modeSel.innerHTML = MODE_FILTER_OPTIONS.map((o) => `<option value="${o.value}">${o.label}</option>`).join('');

  // Filter-Change: activeListingId verwerfen, damit reloadList() automatisch
  // die erste Row der neuen Sicht selektiert (statt am vorigen Listing zu
  // kleben — das oft gar nicht mehr in der gefilterten Liste steht).
  sfSel.addEventListener('change', () => {
    state.storefront = sfSel.value;
    activePage = 0;
    activeListingId = null;
    selectedIds.clear();
    void reloadList();
  });
  statusSel.addEventListener('change', () => {
    state.status = statusSel.value;
    activePage = 0;
    activeListingId = null;
    selectedIds.clear();
    void reloadList();
  });
  modeSel.addEventListener('change', () => {
    state.mode = (modeSel.value as ModeFilterValue) || 'all';
    activePage = 0;
    activeListingId = null;
    selectedIds.clear();
    void reloadList();
  });
  // Such-Filter: live ueber bereits geladene Rows. Reine DOM-Operation, kein RPC-Reload.
  const searchInput = host.querySelector<HTMLInputElement>('#klst-search')!;
  const searchClear = host.querySelector<HTMLButtonElement>('#klst-search-clear')!;
  const updateClearVis = () => { searchClear.classList.toggle('show', searchInput.value.length > 0); };
  // Fix (Branch-Pruefung 2026-08-24): Nach jeder Aenderung des Suchbegriffs muessen
  // renderBulkBar() UND syncSelectAllCheckbox() erneut laufen. Beide lesen ihren Zustand
  // ueber visibleRowIds() aus dem DOM, und genau der aendert sich hier -- ohne den Nachlauf
  // zeigte der Balken weiter "50 markiert", waehrend die Knoepfe daneben nur auf die drei
  // sichtbaren wirken, und die "Seite markieren"-Checkbox blieb auf dem Stand von vor der
  // Suche haengen. Dieselbe Naht wie der Sichtbarkeits-Fix aus Task 7, nur der zweite Anlass.
  const nachSuchwechsel = () => { renderBulkBar(); syncSelectAllCheckbox(); };
  searchInput.addEventListener('input', () => {
    state.search = searchInput.value;
    updateClearVis();
    applyClientSearchFilter(host, state.search);
    nachSuchwechsel();
  });
  searchClear.addEventListener('click', () => {
    searchInput.value = '';
    state.search = '';
    updateClearVis();
    applyClientSearchFilter(host, '');
    nachSuchwechsel();
    searchInput.focus();
  });
  pgFirst.addEventListener('click', () => { activePage = 0; selectedIds.clear(); void reloadList(); });
  pgPrev.addEventListener('click', () => { if (activePage > 0) { activePage -= 1; selectedIds.clear(); void reloadList(); } });
  pgNext.addEventListener('click', () => { activePage += 1; selectedIds.clear(); void reloadList(); });
  pgLast.addEventListener('click', () => {
    const total = lastRows[0]?.total_count ?? 0;
    activePage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
    selectedIds.clear();
    void reloadList();
  });

  // Sammelaktion (Task 7): "Seite markieren"-Checkbox togglet die aktuell SICHTBARE
  // Menge. Lebt ausserhalb von reloadList(), weil reloadList() nur #klst-list neu
  // rendert, nicht die Shell drumherum.
  //
  // Fix (Review 2026-08-24, Important 1): frueher togglete das alle Zeilen der geladenen
  // Page (`lastRows`), unabhaengig von einer aktiven Freitextsuche. Die Suche versteckt
  // Zeilen nur im DOM (siehe applyClientSearchFilter), sie veraendert `lastRows` nicht --
  // wer also "abc" tippt (3 von 50 Karten sichtbar) und dann "Seite markieren" klickt, bekam
  // bisher 50 markiert statt 3. Jetzt zaehlt nur, was `visibleRowIds()` gerade im DOM findet.
  const selectAllCb = host.querySelector<HTMLInputElement>('#klst-select-all');
  selectAllCb?.addEventListener('change', () => {
    const listHost = host.querySelector<HTMLDivElement>('#klst-list')!;
    const sichtbar = visibleRowIds(host);
    if (selectAllCb.checked) {
      for (const id of sichtbar) selectedIds.add(id);
    } else {
      for (const id of sichtbar) selectedIds.delete(id);
    }
    listHost.querySelectorAll<HTMLInputElement>('.li-select').forEach((cb) => {
      const id = cb.dataset['selectId'];
      cb.checked = !!id && selectedIds.has(id);
    });
    renderBulkBar();
  });

  await reloadList();

  /** Aktualisiert die Sammelaktion-Leiste (`.bulkbar`, geteiltes Muster mit offers.ts)
   *  nach jeder Aenderung an `selectedIds` ODER am Such-Filter. Leer = kein Inhalt = keine
   *  Hoehe (`.bulkbar:empty{display:none}` in kf-page.css), also kein Sichtbarkeits-Flag
   *  noetig.
   *
   *  Fix (Branch-Pruefung 2026-08-24): Die Zahl zaehlt jetzt die SICHTBARE Auswahl, nicht
   *  `selectedIds` roh. Vorher stand "50 markiert" neben Knoepfen, die durch
   *  filtereSichtbareAuswahl() nur auf die drei von der Suche uebrig gelassenen Zeilen
   *  wirken -- die Leiste behauptete also einen anderen Wirkungsbereich als die Aktion
   *  darunter hat. Ausgeblendete, aber markierte Zeilen werden ausdruecklich benannt statt
   *  verschwiegen: sie bleiben in `selectedIds` stehen und kommen zurueck, sobald die Suche
   *  geleert wird. */
  function renderBulkBar(): void {
    const el = host.querySelector<HTMLDivElement>('#klst-bulkbar');
    if (!el) return;
    const sichtbareIds = visibleRowIds(host);
    const sichtbarMarkiert = [...selectedIds].filter((id) => sichtbareIds.has(id)).length;
    const ausgeblendetMarkiert = selectedIds.size - sichtbarMarkiert;
    if (sichtbarMarkiert === 0 && ausgeblendetMarkiert === 0) { el.innerHTML = ''; return; }
    // Sonderfall: es IST etwas markiert, aber die Suche verdeckt alles davon. Die Leiste
    // verschwinden zu lassen waere hier die schlechtere Antwort -- der Haendler haette den
    // Eindruck, seine Auswahl sei weg, obwohl sie steht und beim Leeren der Suche
    // zurueckkommt. Also: Leiste bleibt, Knoepfe entfallen (sie haetten nichts, worauf sie
    // wirken koennten), Text sagt warum.
    if (sichtbarMarkiert === 0) {
      el.innerHTML = `
        <div class="bulkbar">
          <span class="selinfo"><span class="pd"></span>${ausgeblendetMarkiert} markiert<span class="muted">, alle durch die Suche ausgeblendet — Suche leeren, um sie zu bearbeiten</span></span>
        </div>
      `;
      return;
    }
    const zusatz = ausgeblendetMarkiert > 0
      ? ` <span class="muted">(${ausgeblendetMarkiert} durch die Suche ausgeblendet, nicht betroffen)</span>`
      : '';
    el.innerHTML = `
      <div class="bulkbar">
        <span class="selinfo"><span class="pd"></span>${sichtbarMarkiert} markiert${zusatz}</span>
        <button type="button" class="bulk-btn" data-action="bulk-attach">Auf Aufschalten setzen</button>
        <button type="button" class="bulk-btn" id="klst-bulk-new">Auf Neu listen setzen</button>
      </div>
    `;
    el.querySelector<HTMLButtonElement>('[data-action="bulk-attach"]')?.addEventListener('click', () => {
      void runBulkModeChange('attach');
    });
    // Zweistufige Bestaetigung wie im Detail-Pane (kein window.confirm, Projektregel):
    // "Neu listen" legt ein nicht loeschbares Kaufland-Katalogprodukt an.
    const newBtn = el.querySelector<HTMLButtonElement>('#klst-bulk-new');
    if (newBtn) {
      wireTwoStepConfirm(
        newBtn,
        'Wirklich neu listen? Legt nicht löschbare Katalogprodukte an',
        () => void runBulkModeChange('new'),
      );
    }
  }

  /** Haelt die "Seite markieren"-Checkbox mit `selectedIds` synchron -- checked, wenn
   *  ausnahmslos alle SICHTBAREN Zeilen markiert sind, sonst indeterminate/leer.
   *  Fix (Review 2026-08-24, Important 1): bezieht sich jetzt auf `visibleRowIds()`
   *  statt auf `lastRows` ungefiltert -- sonst zeigte die Checkbox bei aktiver Suche
   *  "alle markiert" an, obwohl nur die sichtbaren drei von fuenfzig es waren. */
  function syncSelectAllCheckbox(): void {
    if (!selectAllCb) return;
    const sichtbar = [...visibleRowIds(host)];
    const alleMarkiert = sichtbar.length > 0 && sichtbar.every((id) => selectedIds.has(id));
    selectAllCb.checked = alleMarkiert;
    selectAllCb.indeterminate = !alleMarkiert && sichtbar.some((id) => selectedIds.has(id));
  }

  /**
   * Fuehrt die Sammelaktion aus: engt die Auswahl zuerst auf das SICHTBARE ein (Fix
   * Important 1), teilt den Rest per teileAuswahlNachModus in machbar/nicht machbar,
   * setzt den Modus fuer alles Machbare per RPC und meldet ALLE drei Zahlen zurueck --
   * gesetzt, ausgeblendet, nicht machbar und RPC-fehlgeschlagen, je mit Grund. Nichts
   * wird still uebersprungen: weder Zeilen, die eine aktive Suche gerade verdeckt
   * (Important 1), noch Zeilen ohne Katalogtreffer (client-seitig erkannt), noch ein
   * RPC-Fehler je Zeile (z. B. Status nicht mehr pending/error, siehe
   * kaufland_set_listing_mode, 0058).
   */
  async function runBulkModeChange(modus: 'attach' | 'new'): Promise<void> {
    const alleAusgewaehlt: AuswahlZeile[] = lastRows
      .filter((r) => selectedIds.has(r.id))
      .map((r) => ({ id: r.id, attach_id_product: r.attach_id_product ?? null }));
    if (alleAusgewaehlt.length === 0) return;

    // Fix (Review 2026-08-24, Important 1): eine per Suche ausgeblendete, aber weiterhin
    // markierte Zeile wird NICHT mitgenommen -- der Haendler sieht sie in diesem Moment
    // nicht, eine Aenderung an ihr waere eine Ueberraschung (bei 'new' sogar eine
    // unumkehrbare). Sie bleibt in `selectedIds` stehen (siehe unten), statt die Auswahl
    // stillschweigend zu verwerfen.
    const { sichtbar, ausgeblendet } = filtereSichtbareAuswahl(alleAusgewaehlt, visibleRowIds(host));
    if (sichtbar.length === 0) {
      showToast(
        `Die markierten Angebote sind aktuell durch die Suche ausgeblendet. Suche leeren, um sie zu erreichen.`,
        'info',
      );
      return;
    }

    const { machbar, nichtMachbar } = teileAuswahlNachModus(sichtbar, modus);

    let gesetzt = 0;
    const rpcFehler: string[] = [];
    for (const z of machbar) {
      const { error } = await ctx.supabase.rpc('kaufland_set_listing_mode', {
        _listing_id: z.id,
        _mode: modus,
      });
      if (error) rpcFehler.push(error.message);
      else gesetzt += 1;
    }

    // Nur das tatsaechlich Versuchte (sichtbar) aus der Auswahl nehmen -- ausgeblendete
    // Zeilen bleiben markiert, damit sie erreichbar sind, sobald die Suche das wieder zulaesst.
    for (const z of sichtbar) selectedIds.delete(z.id);
    await reloadList();

    const modusLabel = modus === 'attach' ? 'Aufschalten' : 'Neu listen';
    const teile: string[] = [
      `${gesetzt} Angebot${gesetzt === 1 ? '' : 'e'} auf ${modusLabel} gesetzt.`,
    ];
    if (nichtMachbar.length > 0) {
      // Fix (Review 2026-08-24, Important 3): kein eigener Grundtext mehr -- derselbe
      // Zustand, den bestimmeModusWahl() fuer die Einzel-Entscheidung im Detail-Pane schon
      // in Worte fasst, statt ihn hier ein zweites Mal (und mit einer engeren Behauptung:
      // "kein Katalogtreffer" nennt eine moegliche Ursache, waehrend attach_id_product auch
      // durch eine fruehere new-Entscheidung geleert sein kann) zu formulieren.
      const grund = bestimmeModusWahl(null).grundWennNicht ?? '';
      teile.push(
        `${nichtMachbar.length} Angebot${nichtMachbar.length === 1 ? '' : 'e'} `
        + `${nichtMachbar.length === 1 ? 'wurde' : 'wurden'} nicht geändert: ${grund}`,
      );
    }
    if (ausgeblendet.length > 0) {
      teile.push(
        `${ausgeblendet.length} ${ausgeblendet.length === 1 ? 'ist' : 'sind'} durch die Suche `
        + `ausgeblendet und blieb${ausgeblendet.length === 1 ? '' : 'en'} unveraendert; Suche leeren, `
        + `um ${ausgeblendet.length === 1 ? 'es' : 'sie'} zu erreichen.`,
      );
    }
    if (rpcFehler.length > 0) {
      const ersterGrund = rpcFehler[0];
      const restGruende = rpcFehler.length - 1;
      // Fix (Review 2026-08-24, Kleinigkeit): "weitere${n===1?'r':''} Grund" ergab bei
      // z. B. 3 Fehlern "2 weitere Grund" -- "Grund" braucht bei restGruende > 1 ebenfalls
      // die Pluralform "Gruende", nicht nur "weitere" ohne Endung.
      const weitere = restGruende > 0
        ? ` (und ${restGruende} weitere${restGruende === 1 ? 'r' : ''} Grund${restGruende === 1 ? '' : 'e'})`
        : '';
      teile.push(
        `${rpcFehler.length} konnte${rpcFehler.length === 1 ? '' : 'n'} nicht gesetzt werden: ${ersterGrund}${weitere}`,
      );
    }
    // Fix (Review 2026-08-24, Kleinigkeit): ein RPC-Fehlschlag machte den Toast bisher nur
    // dann nicht-gruen, wenn NICHTS gesetzt wurde. 19 gesetzt + 1 RPC-Fehler zeigte "success"
    // -- ein Teilfehlschlag sah aus wie ein voller Erfolg und verschwand nach 5s von selbst.
    const level = gesetzt === 0 || rpcFehler.length > 0 ? 'error' : 'success';
    showToast(teile.join(' '), level);
  }

  // Delegiert an das geteilte Pane-Modul. ctx: KauflandListCtx ist strukturkompatibel
  // zu DetailPaneCtx (gleiche Felder: supabase, session, supabaseUrl).
  function showDetail(id: string) {
    const detailHost = host.querySelector<HTMLDivElement>('#klst-detail')!;
    void renderListingDetailPane(detailHost, ctx, id, {
      kind: 'lister',
      onListingChanged: () => void reloadList(),
      onDeleted: () => {
        activeListingId = null;
        clearDetail('Wähle ein Listing aus der Liste links.');
        void reloadList();
      },
    });
  }

  // ---------------------------------------------------------------------
  async function reloadList() {
    const listHost = host.querySelector<HTMLDivElement>('#klst-list')!;
    listHost.innerHTML = `<div class="li-loading">Lade…</div>`;
    const { data, error } = await ctx.supabase.rpc('kaufland_list_listings', {
      _status: state.status || null,
      _storefront: state.storefront || null,
      _limit: PAGE_SIZE,
      _offset: activePage * PAGE_SIZE,
    });
    if (error) {
      listHost.innerHTML = `<div class="li-loading err">Fehler: ${escapeHtml(error.message)}</div>`;
      updatePager(0);
      return;
    }
    lastRows = (data ?? []) as ListingRow[];
    // Mode-Hydration: die RPC liefert kaufland.listings.mode nicht, also
    // ein einzelner select fuer alle IDs der aktuellen Page (max PAGE_SIZE Rows).
    // Fail-soft: bei Lookup-Error bleiben alle Rows ohne mode (→ Default-Pill "Neu",
    // Default-Filterzugehoerigkeit "new").
    if (lastRows.length > 0) {
      const ids = lastRows.map((r) => r.id);
      const { data: modeRows, error: modeErr } = await ctx.supabase
        .schema('kaufland')
        .from('listings')
        .select('id, mode, attach_id_product, kaufland_status_reason, kaufland_unit_id, storefront')
        .in('id', ids);
      if (modeErr) {
        console.warn('[kaufland-list] mode hydration failed:', modeErr.message);
      } else if (modeRows) {
        const byId = new Map<string, string>();
        const attachById = new Map<string, number | null>();
        const reasonById = new Map<string, string | null>();
        const unitIdById = new Map<string, string>();
        for (const m of modeRows as Array<{
          id: string;
          mode: string;
          attach_id_product: number | null;
          kaufland_status_reason: string | null;
          kaufland_unit_id: string | null;
        }>) {
          if (typeof m.mode === 'string') byId.set(m.id, m.mode);
          attachById.set(m.id, typeof m.attach_id_product === 'number' ? m.attach_id_product : null);
          reasonById.set(m.id, typeof m.kaufland_status_reason === 'string' ? m.kaufland_status_reason : null);
          if (typeof m.kaufland_unit_id === 'string' && m.kaufland_unit_id !== '') {
            unitIdById.set(m.id, m.kaufland_unit_id);
          }
        }

        // Zweiter Schritt: Zustand der Kaufland-Units. Nur fuer Zeilen, die eine Unit-ID
        // haben -- das sind hoechstens die aufgeschalteten, meist eine Handvoll pro Seite.
        // Fail-soft wie die Modus-Hydration: ohne Unit-Daten bleibt die alte Pille stehen.
        const unitStateById = new Map<string, UnitState>();
        const unitIds = [...new Set(unitIdById.values())];
        if (unitIds.length > 0) {
          const { data: unitRows, error: unitErr } = await ctx.supabase
            .schema('kaufland')
            .from('units')
            .select('id_unit, status, stock')
            .in('id_unit', unitIds);
          if (unitErr) {
            console.warn('[kaufland-list] unit hydration failed:', unitErr.message);
          } else if (unitRows) {
            const byUnit = new Map<string, UnitState>();
            for (const u of unitRows as Array<{ id_unit: string; status: string | null; stock: number | null }>) {
              byUnit.set(String(u.id_unit), { status: u.status ?? null, stock: u.stock ?? null });
            }
            for (const [listingId, unitId] of unitIdById) {
              const st = byUnit.get(unitId);
              if (st) unitStateById.set(listingId, st);
            }
          }
        }

        // Fix (2026-08-23, Task 7 Selbstpruefung): frueher hier `byId.get(r.id) === 'attach'
        // ? 'attach' : 'new'` -- das coercte 'undecided' (und jeden anderen Wert) stillschweigend
        // zu 'new'. Ein per MCP provisioniertes Listing (mode='undecided') erschien dann als
        // "Neu" und war von einem echten new-Listing nicht zu unterscheiden. Alle drei Werte
        // jetzt explizit durchreichen; nur ein unbekannter/fehlender Wert faellt auf 'new' zurueck.
        lastRows = lastRows.map((r) => {
          const m = byId.get(r.id);
          const mode: 'new' | 'attach' | 'undecided' =
            m === 'attach' ? 'attach' : m === 'undecided' ? 'undecided' : 'new';
          return {
            ...r,
            mode,
            attach_id_product: attachById.get(r.id) ?? null,
            kaufland_status_reason: reasonById.get(r.id) ?? null,
            unit: unitStateById.get(r.id) ?? null,
          };
        });
      }
    }
    // Pager-Total VOR Mode-Filter abgreifen — das RPC zaehlt unfiltered, der
    // Filter ist clientseitig. Folge: eine Seite mit aktivem Mode-Filter kann
    // weniger Rows zeigen als PAGE_SIZE. Sauberer waere _mode_filter im RPC
    // (Migration 0116, TODO in IMPLEMENTATION.md).
    const total = lastRows[0]?.total_count ?? 0;
    if (state.mode !== 'all') {
      lastRows = lastRows.filter((r) => (r.mode ?? 'new') === state.mode);
    }
    renderMasterList(listHost, lastRows, activeListingId, selectedIds);
    updatePager(total);

    // Click-Wiring. Die Auswahl-Checkbox (.li-select) ist seit dem Fix zu Important 2
    // (Review 2026-08-24) ein GESCHWISTER dieses Buttons im gemeinsamen .li-row-Wrapper,
    // kein Kind mehr -- ein Klick darauf kann strukturell nicht mehr in diesen Handler
    // hineinbubbeln, ein target-Guard ist dafuer nicht mehr noetig (anders als vorher, als
    // die Checkbox noch im Button steckte und Browser sich beim Hit-Testing dort uneins waren).
    listHost.querySelectorAll<HTMLElement>('[data-listing-id]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = el.dataset['listingId'];
        if (!id) return;
        activeListingId = id;
        listHost.querySelectorAll('.li').forEach((r) => r.classList.remove('sel'));
        el.classList.add('sel');
        showDetail(id);
      });
    });

    // Auswahl-Checkboxen: eigener 'change'-Listener. Kein stopPropagation() mehr noetig --
    // als Geschwister des Buttons gibt es keinen Elternhandler mehr, an den ein Klick
    // hierher hindurchbubbeln koennte.
    listHost.querySelectorAll<HTMLInputElement>('.li-select').forEach((cb) => {
      cb.addEventListener('change', () => {
        const id = cb.dataset['selectId'];
        if (!id) return;
        if (cb.checked) selectedIds.add(id); else selectedIds.delete(id);
        renderBulkBar();
        syncSelectAllCheckbox();
      });
    });
    // Aktiven Such-Filter nach Reload erneut anwenden — sonst zeigt der frische
    // Render alle Rows, obwohl der User noch einen Term im Suchfeld stehen hat.
    // MUSS vor syncSelectAllCheckbox()/renderBulkBar() laufen (Fix Review 2026-08-24,
    // Important 1): beide lesen jetzt ueber visibleRowIds() den sichtbaren DOM-Zustand:
    // ohne die Suche vorher erneut anzuwenden, saehen sie kurzzeitig ALLE frisch
    // gerenderten Zeilen als sichtbar an, obwohl der Haendler noch einen Suchbegriff stehen
    // hat.
    if (state.search) applyClientSearchFilter(host, state.search);
    syncSelectAllCheckbox();
    renderBulkBar();

    // Auto-Focus aus localStorage: wenn der User aus dem Vario-Slot via "Im Lister
    // bearbeiten" hierhergeschaltet hat, steht in `kaufland.focus` welches Listing
    // gemeint ist. localStorage funktioniert als Bruecke zwischen Slot-iframe und
    // Lister-Tab, weil beide auf derselben App-Origin laufen — Vario reicht eigene
    // URL-Params nicht an die iframe-URL durch.
    const focusListingId = consumeFocusListingId();
    if (focusListingId) {
      const hit = lastRows.find((r) => r.vario_listing_id === focusListingId);
      if (hit) {
        activeListingId = hit.id;
        listHost.querySelector<HTMLElement>(`[data-listing-id="${hit.id}"]`)?.classList.add('sel');
        showDetail(hit.id);
        return;
      }
      // Listing nicht auf der aktuellen Seite — Server-Lookup ueber alle Status hinweg.
      void resolveListingByVarioListingId(focusListingId).then((resolved) => {
        if (resolved) {
          activeListingId = resolved;
          showDetail(resolved);
        } else {
          // Listing existiert nicht (oder ist archived und der aktuelle Status-Filter
          // schließt es aus). Fallback auf erste Row, damit der Tab nicht leer ist.
          if (lastRows[0]) {
            activeListingId = lastRows[0].id;
            listHost.querySelector<HTMLElement>(`[data-listing-id="${lastRows[0].id}"]`)?.classList.add('sel');
            showDetail(lastRows[0].id);
          }
        }
      });
      return;
    }

    // Auto-Select erste Row, wenn nichts aktiv
    if (!activeListingId && lastRows[0]) {
      activeListingId = lastRows[0].id;
      const firstEl = listHost.querySelector<HTMLElement>(`[data-listing-id="${lastRows[0].id}"]`);
      firstEl?.classList.add('sel');
      showDetail(lastRows[0].id);
    } else if (activeListingId) {
      showDetail(activeListingId);
    } else {
      clearDetail('Wähle ein Listing aus der Liste links.');
    }
  }

  /**
   * Liest `kaufland.focus` aus localStorage und entfernt den Eintrag. Nur Eintraege
   * juenger als 30s werden akzeptiert, damit stehengebliebene Werte (Tab-Switch,
   * Browser-Reload nach Stunden) den User nicht erneut zwingen.
   */
  function consumeFocusListingId(): string | null {
    try {
      const raw = localStorage.getItem('kaufland.focus');
      if (!raw) return null;
      localStorage.removeItem('kaufland.focus');
      const parsed = JSON.parse(raw) as { listingId?: string; ts?: number };
      if (!parsed?.listingId || typeof parsed.listingId !== 'string') return null;
      if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > 30_000) return null;
      return parsed.listingId;
    } catch {
      return null;
    }
  }

  /**
   * Server-side Lookup einer Listing-Row anhand vario_listing_id, falls sie nicht
   * in der aktuell geladenen Page steckt (z.B. andere Status oder weiter hinten).
   * Setzt KEINEN Filter-State zurueck; lädt nur die Detail-ID.
   */
  async function resolveListingByVarioListingId(varioListingId: string): Promise<string | null> {
    const { data, error } = await ctx.supabase
      .schema('kaufland')
      .from('listings')
      .select('id')
      .eq('vario_listing_id', varioListingId)
      .maybeSingle();
    if (error) {
      console.warn('[kaufland-list] focus-resolve failed:', error.message);
      return null;
    }
    return (data as { id?: string } | null)?.id ?? null;
  }

  function updatePager(total: number) {
    const info = host.querySelector<HTMLElement>('#klst-pg-info')!;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    info.textContent = `${activePage * PAGE_SIZE + 1}–${Math.min(total, (activePage + 1) * PAGE_SIZE)} von ${total} · Seite ${activePage + 1} / ${pages}`;
    pgFirst.disabled = activePage === 0;
    pgPrev.disabled = activePage === 0;
    pgNext.disabled = activePage + 1 >= pages;
    pgLast.disabled = activePage + 1 >= pages;
  }

  function clearDetail(msg: string) {
    const detailHost = host.querySelector<HTMLDivElement>('#klst-detail')!;
    detailHost.innerHTML = `<div class="li-loading">${escapeHtml(msg)}</div>`;
  }
}

// ---------------------------------------------------------------------------
// Rendering Helpers
// ---------------------------------------------------------------------------

function renderShell(standalone: boolean): string {
  const apphead = standalone
    ? `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Kaufland · Lister</div><h1>Listing-Übersicht</h1></div>
        <div class="hactions">
          <span class="hstatus"><span class="d"></span>Kaufland verbunden</span>
        </div>
      </header>`
    : '';

  const filterbar = `<div class="filterbar">
      <div class="select"><select id="klst-sf" title="Storefront filtern"></select></div>
      <div class="select"><select id="klst-status" title="Status filtern"></select></div>
      <div class="select"><select id="klst-mode" title="Modus filtern"></select></div>
      <div class="tbsearch">${KF_ICON.search}<input id="klst-search" type="search" placeholder="Listing nach SKU, Name oder Kategorie suchen…" autocomplete="off" /><button type="button" id="klst-search-clear" class="clearbtn" aria-label="Suche leeren" title="Suche leeren">${KF_ICON.close}</button></div>
      <span class="slot-note">${SPARK_ICON}Inbox wird aus Vario befüllt</span>
    </div>`;

  const work = `<div class="work">
      <div class="card listcard">
        <div id="klst-bulkbar"></div>
        <div class="li-listhead">
          <label class="li-selectall" title="Alle auf dieser Seite fuer die Sammelaktion markieren">
            <input type="checkbox" id="klst-select-all" /> Seite markieren
          </label>
        </div>
        <div id="klst-list" class="li-scroll"></div>
        <div class="li-foot">
          <button type="button" class="pgbtn" id="klst-pg-first" title="Erste Seite">${PG_FIRST}</button>
          <button type="button" class="pgbtn" id="klst-pg-prev" title="Vorherige Seite">${PG_PREV}</button>
          <span id="klst-pg-info">–</span>
          <button type="button" class="pgbtn" id="klst-pg-next" title="Nächste Seite">${PG_NEXT}</button>
          <button type="button" class="pgbtn" id="klst-pg-last" title="Letzte Seite">${PG_LAST}</button>
        </div>
      </div>
      <div class="card detailcard"><div id="klst-detail" class="kl-detail-host"></div></div>
    </div>`;

  // Standalone: voll im Sales-Channels-Tab (eigenes apphead, Höhe an iframe gekoppelt).
  // Embedded (Dashboard-Vorschau-Shell): kein apphead, feste Master-Detail-Höhe.
  return standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${apphead}${filterbar}${work}</main></div>`
    : `<div class="kf-page" style="display:flex;flex-direction:column;height:min(78vh,840px);padding:2px;">${filterbar}${work}</div>`;
}

async function populateStorefronts(ctx: KauflandListCtx, host: HTMLElement) {
  const sel = host.querySelector<HTMLSelectElement>('#klst-sf')!;
  sel.innerHTML = `<option value="">Alle Märkte</option>`;
  const { data, error } = await ctx.supabase.rpc('kaufland_list_storefronts');
  if (error) {
    console.warn('[kaufland-list] storefront list failed', error);
    return;
  }
  const rows = (data ?? []) as Array<{ storefront: string }>;
  for (const r of rows) {
    const label = STOREFRONT_LABELS[r.storefront] ?? r.storefront.toUpperCase();
    const opt = document.createElement('option');
    opt.value = r.storefront;
    opt.textContent = `${r.storefront.toUpperCase()} · ${label}`;
    sel.append(opt);
  }
}

function renderMasterList(
  host: HTMLElement,
  rows: ListingRow[],
  activeId: string | null,
  selectedIds: ReadonlySet<string>,
): void {
  if (rows.length === 0) {
    host.innerHTML = `
      <div class="li-empty">
        <div class="li-empty-title">Keine Listings in dieser Ansicht</div>
        <div class="li-empty-hint">Trage in Vario am Artikel ein Listing mit Kaufland-Verkaufskanal und Kategorie ein.</div>
      </div>`;
    return;
  }
  host.innerHTML = rows.map((r) => renderMasterRow(r, r.id === activeId, selectedIds.has(r.id))).join('');
}

function renderMasterRow(r: ListingRow, active: boolean, selected: boolean): string {
  const stCls = STATUS_CLS[r.status] ?? 'st-pending';
  // Nicht STATUS_PILL direkt: bei 'published' entscheidet der Unit-Zustand mit, ob das Wort
  // "Gelistet" ueberhaupt stimmt. Begruendung am Helfer.
  const stLabel = listingPill(r.status, r.unit).label;
  const sfCode = r.storefront.toUpperCase();
  const number = escapeHtml(r.vario_article_number ?? r.vario_article_id);
  const name = escapeHtml(r.vario_article_name ?? '—');
  const cat = escapeHtml(r.vario_category_name ?? '—');
  // Mode-/Variantenvater-Pill: Variantenvater (lila) ist der Sonderfall ohne
  // Mapping-Flow; sonst 'attach' (grün, reduzierter Aufschalt-Flow), 'new' (grau),
  // oder 'undecided' (gelb, noch nicht entschieden).
  const modePill = r.is_variant_parent
    ? `<span class="pill vp-pill">Variantenvater</span>`
    : (r.mode === 'attach'
        ? `<span class="pill mode-pill attach">Aufschalten</span>`
        : r.mode === 'undecided'
          ? `<span class="pill mode-pill undecided">Entscheidung ausstehend</span>`
          : `<span class="pill mode-pill">Neu</span>`);
  // Waehrend der Pruefung dieselbe Zeile wie bei einem Fehler, nur neutral: Kauflands
  // Wartegrund. Ohne ihn steht dort tagelang nur "In Pruefung" und es ist von aussen nicht
  // zu unterscheiden, ob Kaufland noch arbeitet oder ob etwas fehlt (am 2026-08-07 waren es
  // fehlende Attribute, die Kaufland ausdruecklich NICHT als Fehler fuehrt).
  const waitLine = (r.status === 'validating' && r.kaufland_status_reason)
    ? `<div class="li-err" style="color:#475467;">${escapeHtml(truncate(r.kaufland_status_reason, 140))}</div>`
    : '';
  const errLine = (r.status === 'error' && r.last_error)
    ? `<div class="li-err">${WARN_ICON}<span>${escapeHtml(truncate(r.last_error, 140))}</span></div>`
    : '';
  // Such-Index: alles was matchen darf in eine lowercase-Zeile.
  const searchHaystack = [
    r.vario_article_number ?? '',
    r.vario_article_id ?? '',
    r.vario_article_name ?? '',
    r.vario_category_name ?? '',
  ].join(' ').toLowerCase();
  // Fix (Review 2026-08-24, Important 2): die Auswahl-Checkbox ist jetzt ein GESCHWISTER
  // des .li-Buttons, nicht mehr sein Kind. Ein <input> innerhalb eines <button> ist
  // ungueltiges HTML (Buttons duerfen kein interaktives Inhaltsmodell enthalten), und
  // Browser sind sich beim Hit-Testing dort uneins -- Firefox leitet Klicks auf
  // Button-Nachfahren traditionell auf den Button selbst um, wodurch ein Klick auf die
  // Checkbox nie am Input ankaeme und weder der `closest('.li-select')`-Guard noch
  // `stopPropagation()` am Input je gegriffen haetten. Als Geschwister im gemeinsamen
  // `.li-row`-Wrapper kann kein Checkbox-Klick mehr in den Button hineinbubbeln --
  // strukturell ausgeschlossen statt weggetestet. Nebeneffekt: die Checkbox ist jetzt ein
  // eigenstaendig fokussierbares/tastaturbedienbares Element statt in der Button-Aktivierung
  // verschluckt zu werden.
  return `
    <div class="li-row" data-row-id="${r.id}" data-search-label="${escapeHtml(searchHaystack)}">
      <input type="checkbox" class="li-select" data-select-id="${r.id}" ${selected ? 'checked' : ''} aria-label="Für Sammelaktion auswählen" title="Für Sammelaktion auswählen" />
      <button type="button" class="li ${active ? 'sel' : ''}" data-listing-id="${r.id}">
        <div class="li-name">${name}</div>
        <div class="li-top">
          <span class="pill ${stCls}"><span class="d"></span>${escapeHtml(stLabel)}</span>
          ${modePill}
        </div>
        <div class="li-sku">${number}</div>
        ${renderRowMiniProgress(r.status)}
        <div class="li-meta">
          <span class="li-cat">${TAG_ICON}${cat}</span>
          <span class="li-market"><span class="flag">${kfFlagSvg(r.storefront)}</span>${escapeHtml(sfCode)}</span>
        </div>
        ${waitLine}
        ${errLine}
      </button>
    </div>
  `;
}

/** Client-seitiger Live-Such-Filter ueber die Master-Rows. data-search-label
 *  ist ein lowercase-Index aus number+id+name+category. Term wird in tokens
 *  zerlegt, jedes token muss matchen (AND). */
function applyClientSearchFilter(host: HTMLElement, term: string): void {
  const tokens = term.trim().toLowerCase().split(/\s+/).filter((t) => t.length > 0);
  // Fix (Review 2026-08-24, Important 2): versteckt/zeigt jetzt den Karten-Wrapper
  // (.li-row), nicht mehr nur den Button (.li) darin -- sonst bliebe die Auswahl-Checkbox
  // (jetzt Geschwister des Buttons, siehe renderMasterRow) bei einer gefilterten Zeile
  // sichtbar stehen, waehrend der Rest der Karte verschwindet.
  host.querySelectorAll<HTMLElement>('.li-row').forEach((row) => {
    if (tokens.length === 0) { row.hidden = false; return; }
    const hay = row.dataset['searchLabel'] ?? '';
    row.hidden = !tokens.every((t) => hay.includes(t));
  });
}

/**
 * Liest die aktuell sichtbaren Row-IDs aus dem DOM (nicht durch die Freitextsuche
 * ausgeblendet, siehe applyClientSearchFilter). Grundlage fuer "Seite markieren" und die
 * Sammelaktion (Fix Important 1, Review 2026-08-24) -- beide duerfen nur auf das wirken,
 * was der Haendler gerade sieht, nicht auf `lastRows`/`selectedIds` ungefiltert.
 */
function visibleRowIds(host: HTMLElement): Set<string> {
  const ids = new Set<string>();
  host.querySelectorAll<HTMLElement>('.li-row[data-row-id]:not([hidden])').forEach((el) => {
    const id = el.dataset['rowId'];
    if (id) ids.add(id);
  });
  return ids;
}

/** Mini-Progress unter Listing-Card. Status-basiert, weil der RPC bisher keinen
 *  mapped/required-Count liefert. Heuristik:
 *   - published/queued/mapped → 100% gruen
 *   - mapping/error           → 50% orange
 *   - pending/archived        → 0% (nur grauer track) */
function renderRowMiniProgress(status: ListingStatus): string {
  let pct = 0;
  let label = 'neu';
  switch (status) {
    case 'published':
    case 'queued':
    case 'mapped':
      pct = 100; label = 'bereit'; break;
    case 'mapping':
    case 'error':
      pct = 50; label = 'in Arbeit'; break;
    default:
      pct = 0; label = 'neu'; break;
  }
  return `
    <div class="li-prog" title="Status: ${status}">
      <div class="li-bar"><i style="width:${pct}%;"></i></div>
      <span>${label}</span>
    </div>
  `;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
